import { WidgetFilters } from './WidgetFilters';
import { IColumnDef } from './table';
import { Columns } from './table/Columns';
import { Metrics, IMetricDef } from './chart/Metrics';

import { useWidgetFiltersController } from './WidgetFilters/useWidgetFiltersController';

import { Widget } from '@/modules';
import classNames from 'classnames';
import { useMemo } from 'react';
import { ITableDataWithExpansion } from './table/types';

interface WidgetSubHeaderProps {
  onColumnReorder: (columns: IColumnDef[]) => void;
  onColumnChangeVisibility?: (columns: IColumnDef[]) => void;
  onMetricChangeVisibility?: (metricName: string) => void;
  widget: Widget;
  widgetData: any;
  widgetFiltersController: ReturnType<typeof useWidgetFiltersController>;
  chartMetrics?: IMetricDef[];
  hiddenMetrics?: Set<string>;
  hiddenColumns?: Set<string>;
  isExportLoading?: boolean;
}

export const WidgetSubHeader = ({
  onColumnReorder,
  onColumnChangeVisibility,
  onMetricChangeVisibility,
  widget,
  widgetData,
  widgetFiltersController,
  chartMetrics,
  hiddenMetrics,
  hiddenColumns,
  isExportLoading
}: WidgetSubHeaderProps) => {
  const extractedData = widgetData?.widget?.data ?? widgetData?.data ?? widgetData;

  const columnsForPicker = useMemo(() => {
    const cols = extractedData?.columns as IColumnDef[] | undefined;
    if (!Array.isArray(cols)) {
      return [];
    }
    const hidden = hiddenColumns ?? new Set<string>();
    const hideColumnsList: string[] = Array.isArray((widget as any)?.metadata?.['hide-columns'])
      ? (widget as any).metadata['hide-columns']
      : [];
    const isAlwaysHidden = (col: IColumnDef) =>
      hideColumnsList.length > 0 &&
      (hideColumnsList.includes(col.field as string) ||
        hideColumnsList.includes(col.metric as string) ||
        hideColumnsList.includes(col.header as string));
    return cols
      .filter((col) => !isAlwaysHidden(col))
      .map((col) => ({
        ...col,
        hidden: hidden.has(col.header),
      }));
  }, [extractedData?.columns, hiddenColumns, widget]);

  const multiFilters = extractedData?.metadata?.filters || extractedData?.metadata?.['table-filters'];

  // Multi-metric channel mode: service-filter dropdown drives metric selection,
  // so the per-channel series selector must not be shown.
  const isMultiMetricChannelMode =
    widget.type === 'chart' &&
    (widget as any).service?.parameters?.level === 'channel' &&
    (extractedData?.metrics?.length ?? 0) > 1;

  // Single-metric channel mode: show the channel series selector so users can hide/show lines.
  const isChannelMode =
    widget.type === 'chart' &&
    (widget as any).service?.parameters?.level === 'channel' &&
    (extractedData?.metrics?.length ?? 0) === 1;

  const drillDownTable = useMemo(() => {
    if (widget.type !== 'table') return false;
    const data = widgetData as ITableDataWithExpansion;

    const expansionRows = data?.rows?.flatMap(row => row?.expansion?.rows || []) ?? []


    return expansionRows.length > 0
  }, [widget.type, widgetData])

  const shouldHideColumnsSelector = useMemo(() => {
    // a) single column
    if (columnsForPicker.length <= 1) return true;
    // b) exactly one dimension + one metric
    if (columnsForPicker.length === 2) {
      const dimensionCount = columnsForPicker.filter(c => c.type === 'dimension').length;
      return dimensionCount === 1;
    }
    return false;
  }, [columnsForPicker])


  return (
    <div className={classNames('flex justify-end items-center gap-3 px-4 animated animate-bounce ease-in-out fade-in duration-100', { 'pr-6': widget.type === 'table' })}>
      {/* Portal container for custom widget filters */}
      <div id={`widget-filters-portal-${widget.id}`} className="flex items-center gap-3" />
      {isExportLoading && <p className="flex font-roboto text-sm text-left text-[#6d7d8a]">
        Exporting
        <span className="ml-0.5 inline-flex">
          <span className="inline-block animate-bounce" style={{ animationDelay: '0ms' }}>.</span>
          <span className="inline-block animate-bounce" style={{ animationDelay: '150ms' }}>.</span>
          <span className="inline-block animate-bounce" style={{ animationDelay: '300ms' }}>.</span>
        </span>
      </p>}
      <WidgetFilters widgetFiltersController={widgetFiltersController} filter={extractedData?.metadata?.filter} filters={multiFilters} widget={widget} widgetData={widgetData} />
      {
        widget.type === "table" && extractedData?.rows && extractedData.rows.length > 0 && !(widget as any)?.metadata?.['hide-column-filter'] && !shouldHideColumnsSelector && (
          <Columns
            widget={widget}
            drillDownTable={drillDownTable}
            columns={columnsForPicker}
            onReorder={onColumnReorder}
            onChangeVisibility={onColumnChangeVisibility}
          />
        )
      }
      {
        widget.type === "chart" && chartMetrics && chartMetrics.length > 0 && hiddenMetrics && onMetricChangeVisibility && (widget.metadata?.["showMetricSelector"] || isChannelMode || isMultiMetricChannelMode) && (
          <Metrics
            metrics={chartMetrics}
            hiddenMetrics={hiddenMetrics}
            onChangeVisibility={onMetricChangeVisibility}
            metricSelectorLabel={isChannelMode || isMultiMetricChannelMode ? 'Channels' : widget?.metadata?.["metricSelectorLabel"]}
            disabled={isMultiMetricChannelMode}
          />
        )
      }

    </div>
  );
};
